import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { DocumentListQuery } from "@pkhoffice/shared";
import {
  fetchDocuments,
  fetchDocument,
  searchDocuments,
  createDocument,
  updateDocument,
  deleteDocument,
} from "./api";

export function useDocuments(query: Partial<DocumentListQuery>) {
  return useQuery({
    queryKey: ["documents", query],
    queryFn: () => fetchDocuments(query),
    staleTime: 30_000,
  });
}

export function useDocument(id: number | undefined) {
  return useQuery({
    queryKey: ["document", id],
    queryFn: () => fetchDocument(id!),
    enabled: id !== undefined && id > 0,
  });
}

export function useDocumentSearch(q: string) {
  return useQuery({
    queryKey: ["document-search", q],
    queryFn: () => searchDocuments(q),
    enabled: q.trim().length >= 2,
  });
}

export function useCreateDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createDocument,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["documents"] }),
  });
}

export function useUpdateDocument(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => updateDocument(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["document", id] });
      qc.invalidateQueries({ queryKey: ["documents"] });
    },
  });
}

export function useDeleteDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteDocument,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["documents"] }),
  });
}
